import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  lang?: 'it' | 'en';
}

export default function Footer({ lang = 'it' }: FooterProps) {
  const year = 2026;

  return (
    <footer
      style={{
        borderTop: '1px solid rgba(240, 247, 255, 0.08)',
        paddingTop: '60px',
        paddingBottom: '40px',
        background: '#0B1F2E',
      }}
    >
      <div className="container-main">
        {/* Top row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '40px',
            marginBottom: '48px',
          }}
        >
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <AppLogo size={48} />
              <span
                style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontWeight: 700,
                  fontSize: '18px',
                  color: '#F0F7FF',
                }}
              >
                E-Foil Experience
              </span>
            </div>
            <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.45)', lineHeight: 1.7, maxWidth: '220px' }}>
              {lang === 'it' ?'Noleggio e lezioni e-foil sul Lago Maggiore. Un\'esperienza unica tra acqua e cielo.' :'E-foil rental and lessons on Lake Maggiore. A unique experience between water and sky.'}
            </p>
            <a
              href="tel:+393757934676"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '14px', color: '#00D4FF', fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}
            >
              <Icon name="PhoneIcon" size={14} />
              +39 375 793 4676
            </a>
            <a
              href="mailto:info@e-foilexperience.com"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', color: '#00D4FF', fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}
            >
              <Icon name="EnvelopeIcon" size={14} />
              info@e-foilexperience.com
            </a>
          </div>

          {/* Links */}
          <div>
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.3)', marginBottom: '16px' }}>
              {lang === 'it' ? 'Naviga' : 'Navigate'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { labelIT: 'Esperienze & Prezzi', labelEN: 'Experiences & Pricing', href: '/experiences-pricing' },
                { labelIT: 'Prenota', labelEN: 'Book', href: '/booking' },
                { labelIT: 'Location', labelEN: 'Locations', href: '/locations' },
              ].map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  style={{ color: 'rgba(240,247,255,0.55)', fontSize: '14px', textDecoration: 'none', transition: 'color 0.2s ease' }}
                >
                  {lang === 'it' ? l.labelIT : l.labelEN}
                </Link>
              ))}
            </div>
          </div>

          {/* More links */}
          <div>
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.3)', marginBottom: '16px' }}>
              {lang === 'it' ? 'Azienda' : 'Company'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { labelIT: 'Privacy Policy', labelEN: 'Privacy Policy', hrefIT: '/it/privacy-policy', hrefEN: '/it/privacy-policy' },
                { labelIT: 'Termini e Condizioni', labelEN: 'Terms & Conditions', hrefIT: '/it/termini-e-condizioni', hrefEN: '/it/termini-e-condizioni' },
                { labelIT: 'Cookie Policy', labelEN: 'Cookie Policy', hrefIT: '/it/cookie-policy', hrefEN: '/it/cookie-policy' },
              ].map((l) => (
                <Link
                  key={'hrefIT' in l ? l.hrefIT : l.href}
                  href={'hrefIT' in l ? (lang === 'it' ? l.hrefIT! : l.hrefEN!) : l.href!}
                  style={{ color: 'rgba(240,247,255,0.55)', fontSize: '14px', textDecoration: 'none', transition: 'color 0.2s ease' }}
                >
                  {lang === 'it' ? l.labelIT : l.labelEN}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact & Social */}
          <div>
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.3)', marginBottom: '16px' }}>
              {lang === 'it' ? 'Seguici' : 'Follow us'}
            </p>
            <a
              href="https://wa.me/393757934676"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              style={{ width: '100%', marginBottom: '12px', fontSize: '13px', padding: '10px 16px' }}
            >
              <Icon name="ChatBubbleLeftRightIcon" size={15} />
              WhatsApp
            </a>
            <a
              href="https://instagram.com/efoilexperiencelagomaggiore"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'rgba(240,247,255,0.65)',
                fontSize: '14px',
                textDecoration: 'none',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(240,247,255,0.1)',
                transition: 'border-color 0.2s ease',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
              </svg>
              @efoilexperiencelagomaggiore
            </a>
          </div>
        </div>

        {/* Divider */}
        <div className="section-divider" style={{ marginBottom: '24px' }} />

        {/* Bottom row */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.3)' }}>
            © {year} E-Foil Experience · Lago Maggiore, Italia
          </p>
          <div style={{ display: 'flex', gap: '20px' }}>
            <Link href="/it/privacy-policy" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.3)', textDecoration: 'none' }}>Privacy</Link>
            <Link href="/it/termini-e-condizioni" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.3)', textDecoration: 'none' }}>Termini</Link>
            <Link href="/it/cookie-policy" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.3)', textDecoration: 'none' }}>Cookie</Link>
            <Link href="/contact" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.3)', textDecoration: 'none' }}>
              {lang === 'it' ? 'Contatti' : 'Contact'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}